import React from 'react'
import { HiChartBar, HiDuplicate } from 'react-icons/hi'
import { motion, AnimatePresence } from 'framer-motion'
import ChoiceItem from './ChoiceItem'
import { getVoteType } from '../../utils/api'
import { CURRENCIES } from './CurrencySelector'

function ChoiceList({
  choices,
  votingStates,
  cloneMode,
  selectedChoices,
  onVote,
  onToggleVote,
  onRemoveVote,
  onSelectChoice,
  onToggleCloneMode,
  // Edit mode props
  editMode,
  editingChoiceId,
  editingChoiceText,
  setEditingChoiceText,
  isUpdating,
  canEditChoice,
  isPollCreator,
  onStartEditingChoice,
  onCancelEditingChoice,
  onSaveChoice,
  onDeleteChoice,
  deviceId,
  userId,  // New prop for authenticated user ID
  // Currency props
  currency
}) {
  const getMaxVotes = () => {
    const maxPositive = Math.max(...choices.map(choice => Math.max(choice.vote_count || 0, 0)), 0)
    const maxNegative = Math.abs(Math.min(...choices.map(choice => Math.min(choice.vote_count || 0, 0)), 0))
    return Math.max(maxPositive, maxNegative, 1) // Use at least 1 to avoid division by zero
  }

  const maxVotes = getMaxVotes()

  // Get currency symbol
  const currencySymbol = currency ? (CURRENCIES.find(c => c.code === currency)?.symbol || currency) : null

  return (
    <div className="card fade-in-up" style={{ animationDelay: '0.1s', animationFillMode: 'both' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '1.5rem'
      }}>
        <h3 style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          margin: 0
        }}>
          <HiChartBar style={{ fontSize: '1.5rem', color: 'var(--accent-primary)' }} />
          Results
        </h3>
        <button
          className={`btn ${cloneMode ? 'btn-primary' : 'btn-secondary'}`}
          onClick={onToggleCloneMode}
          aria-label={cloneMode ? "Disable clone mode" : "Enable clone mode to select choices for new poll"}
          title={cloneMode ? "Cancel clone mode" : "Create new poll from choices"}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '36px',
            minHeight: '36px',
            padding: 0,
            borderRadius: 'var(--radius-md)',
            boxSizing: 'border-box',
            opacity: cloneMode ? 1 : 0.7,
            transform: cloneMode ? 'scale(1.05)' : 'scale(1)',
            transition: 'all 0.2s ease',
            cursor: 'pointer',
            position: 'relative'
          }}
        >
          <HiDuplicate style={{ fontSize: '1rem' }} />
        </button>
      </div>
      {choices.length === 0 ? (
        <p style={{
          color: 'var(--text-muted)',
          fontStyle: 'italic',
          textAlign: 'center',
          padding: '2rem 0',
          fontSize: '1.125rem'
        }}>
          No choices yet. Add the first choice below!
        </p>
      ) : (
        <motion.div
          style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}
          layout
          transition={{
            duration: 0.5,
            type: "spring",
            damping: 25,
            stiffness: 120
          }}
        >
          <AnimatePresence mode="popLayout">
            {choices.map((choice, index) => {
              const isVoted = getVoteType(choice.id)
              const voteType = getVoteType(choice.id)
              const choiceCanEdit = canEditChoice ? canEditChoice(choice) : false
              const isEditing = editingChoiceId === choice.id
              const isOwnChoice =
                (userId && choice.user_id && choice.user_id === userId) ||
                (choice.creator_device_id === deviceId)

              return (
                <ChoiceItem
                  key={choice.id}
                  choice={choice}
                  isVoted={!!isVoted}
                  voteType={voteType}
                  votingState={votingStates[choice.id]}
                  cloneMode={cloneMode}
                  isSelected={selectedChoices.has(choice.id)}
                  onVote={onVote}
                  onToggleVote={onToggleVote}
                  onRemoveVote={onRemoveVote}
                  onSelect={onSelectChoice ? () => onSelectChoice(choice.id) : undefined}
                  maxVotes={maxVotes}
                  // Edit mode props
                  editMode={editMode}
                  isEditing={isEditing}
                  editingText={isEditing ? editingChoiceText : ''}
                  setEditingText={setEditingChoiceText}
                  isUpdating={isUpdating}
                  canEdit={choiceCanEdit}
                  isOwnChoice={isOwnChoice}
                  onStartEditing={() => onStartEditingChoice && onStartEditingChoice(choice)}
                  onCancelEditing={onCancelEditingChoice}
                  onSave={onSaveChoice}
                  onDelete={() => onDeleteChoice && onDeleteChoice(choice.id)}
                  // Currency props
                  currency={currency}
                  currencySymbol={currencySymbol}
                />
              )
            })}
          </AnimatePresence>
        </motion.div>
      )}
    </div>
  )
}

export default ChoiceList

